import { onMounted, onUnmounted, ref } from 'vue';
import type { ReservationStatus } from '@/lib/types/reservation';

// Delay before a card opens on hover. Stops a burst of cards when the pointer sweeps a dense timeGridWeek (D-02).
export const SHOW_DELAY_MS = 300;

// Grace period before a card closes. Bridges the pixel gap between the event and the card (D-02).
export const HIDE_DELAY_MS = 150;

// Long-press duration shared by the calendar card (D-11) and the timeline tooltip (D-16). Single source of truth.
export const LONG_PRESS_MS = 500;

// Gap between the anchor element and the overlay.
export const ANCHOR_OFFSET_PX = 8;

// Minimum distance any overlay keeps from every viewport edge.
export const VIEWPORT_MARGIN_PX = 8;

// A touch that travels further than this cancels the pending long-press, so it never hijacks a scroll or drag-select.
export const TOUCH_MOVE_CANCEL_PX = 10;

// Stable DOM id for the single teleported preview card. Shared so the card can carry it and the anchor element
// can point at it with aria-describedby — only one card exists at a time, so a constant is sufficient.
export const PREVIEW_CARD_ID = 'calendar-event-preview-card';

/**
 * How a touch gesture on an event ended. The caller needs all three: only 'fired' suppresses the synthetic
 * click, only 'tap' may open a maintenance card, and 'cancelled' must open nothing at all.
 */
export type LongPressOutcome = 'fired' | 'tap' | 'cancelled';

/**
 * Normalized view-model the preview card renders. Nullable fields are nullable on purpose:
 * D-06 omits a row entirely when its value is absent, so the type must express absence
 * rather than force a placeholder.
 */
export interface CalendarPreviewModel {
    eventType: 'reservation' | 'maintenance';
    title: string;
    startMs: number;
    endMs: number;
    resourceName: string;
    accentColor: string;
    otherResourceCount: number;
    status: ReservationStatus | null;
    isPastUnconfirmed: boolean;
    visitorName: string | null;
    ownerName: string | null;
    attendees: number;
    // The cap of a partially-blocking maintenance (D-11). Null for every reservation event and for a fully-blocking
    // maintenance alike — D-06 omits the row rather than rendering an em dash, so absence must be expressible.
    reducedCapacity: number | null;
    // The IANA zone id of the event's resource (D-18). Null for an event whose resource has no timezone at all and
    // for one whose stored id resolves to no entry in the served /timezones list alike — absence must be expressible.
    // `isForeignTimezoneAt` returns false for null, and that is precisely what makes the resource-local row's ABSENCE
    // the correct rendering rather than a placeholder: the card's own range is browser-local and still true.
    resourceTimezoneId: string | null;
    description: string;
}

/**
 * Hover / touch / drag state machine for the calendar preview card.
 * Pure state: renders nothing, imports no store, makes no network call and writes no DOM.
 * Placement and flipping belong to the element that knows its own size.
 */
export function useCalendarEventPreview() {
    const isOpen = ref(false);
    const model = ref<CalendarPreviewModel | null>(null);
    const anchorRect = ref<DOMRect | null>(null);
    const isTouch = ref(false);

    const isGestureBlocked = ref(false);
    const longPressOrigin = ref<{ x: number; y: number } | null>(null);

    let showTimer: ReturnType<typeof setTimeout> | null = null;
    let hideTimer: ReturnType<typeof setTimeout> | null = null;
    let longPressTimer: ReturnType<typeof setTimeout> | null = null;
    let hoverQuery: MediaQueryList | null = null;

    // Explicit gesture phase. Timer-plus-origin nullness cannot express all three outcomes: a press cancelled by
    // travel and a clean short tap both end with a null timer and a null origin, which is exactly the collision
    // that made a swipe open a card. Tracked here rather than inferred at the call site.
    let longPressPhase: 'idle' | 'pending' | 'fired' | 'cancelled' = 'idle';

    function clearShowTimer() {
        if (showTimer !== null) {
            clearTimeout(showTimer);
            showTimer = null;
        }
    }

    function clearHideTimer() {
        if (hideTimer !== null) {
            clearTimeout(hideTimer);
            hideTimer = null;
        }
    }

    function clearLongPressTimer() {
        if (longPressTimer !== null) {
            clearTimeout(longPressTimer);
            longPressTimer = null;
        }
    }

    /** Immediate dismissal with no grace period. Clears every pending timer. */
    function closeNow() {
        clearShowTimer();
        clearHideTimer();
        clearLongPressTimer();
        // A press whose timer was just disarmed can never fire, so the touchend that follows must not be read
        // as a clean tap and reopen the card. Already-resolved phases are left alone: a 'fired' press still
        // owes its caller the synthetic-click suppression.
        if (longPressPhase === 'pending') {
            longPressPhase = 'cancelled';
        }
        isOpen.value = false;
        model.value = null;
        anchorRect.value = null;
    }

    /**
     * Opens the card after SHOW_DELAY_MS (D-01/D-02). No-op while a gesture is blocked.
     * When a card is already open the content swaps in place — the delay is a first-open
     * cost, not a per-event cost, so moving between adjacent events never flickers closed.
     */
    function requestShow(next: CalendarPreviewModel, anchorEl: HTMLElement) {
        if (isGestureBlocked.value) {
            return;
        }

        clearHideTimer();
        clearShowTimer();

        if (isOpen.value) {
            model.value = next;
            anchorRect.value = anchorEl.getBoundingClientRect();
            return;
        }

        showTimer = setTimeout(() => {
            showTimer = null;
            model.value = next;
            anchorRect.value = anchorEl.getBoundingClientRect();
            isOpen.value = true;
        }, SHOW_DELAY_MS);
    }

    /**
     * Opens the card immediately, bypassing SHOW_DELAY_MS.
     * The show delay exists to survive a pointer sweeping across a dense grid; a touch tap is a discrete,
     * deliberate gesture with nothing to debounce, so the delay there would be pure latency (D-11 maintenance tap).
     */
    function openNow(next: CalendarPreviewModel, anchorEl: HTMLElement) {
        if (isGestureBlocked.value) {
            return;
        }

        clearShowTimer();
        clearHideTimer();
        clearLongPressTimer();
        model.value = next;
        anchorRect.value = anchorEl.getBoundingClientRect();
        isOpen.value = true;
    }

    /** Starts the HIDE_DELAY_MS close timer and drops any pending show (D-02). */
    function requestHide() {
        clearShowTimer();
        clearHideTimer();

        hideTimer = setTimeout(() => {
            hideTimer = null;
            closeNow();
        }, HIDE_DELAY_MS);
    }

    /** The enter-cancels-close half of the hoverable-card handshake (D-03). Opens nothing. */
    function cancelHide() {
        clearHideTimer();
    }

    /**
     * Closes the card and blocks new ones for the duration of an event drag, an event
     * resize or a drag-to-select (D-04).
     *
     * It arms its own release: the block's callers cannot guarantee a matching end signal
     * (a cross-day drag-select makes selectAllow return false, after which FullCalendar
     * fires neither select nor unselect), so the release is bound to the pointer event that
     * physically ends the gesture. Capture phase so it runs before any stopPropagation().
     * Re-registering the same function reference for the same type and capture flag is a
     * DOM no-op, so repeated calls leave at most one release listener per event type.
     */
    function blockGesture() {
        isGestureBlocked.value = true;
        closeNow();
        window.addEventListener('pointerup', unblockGesture, { once: true, capture: true });
        window.addEventListener('pointercancel', unblockGesture, { once: true, capture: true });
    }

    /** Clears the block and disarms both release listeners. Idempotent by construction. */
    function unblockGesture() {
        isGestureBlocked.value = false;
        window.removeEventListener('pointerup', unblockGesture, true);
        window.removeEventListener('pointercancel', unblockGesture, true);
    }

    /** Opens the card after LONG_PRESS_MS, bypassing SHOW_DELAY_MS — the press was the delay (D-11). */
    function startLongPress(next: CalendarPreviewModel, anchorEl: HTMLElement, origin: { x: number; y: number }) {
        if (isGestureBlocked.value) {
            return;
        }

        clearLongPressTimer();
        longPressPhase = 'pending';
        longPressOrigin.value = { x: origin.x, y: origin.y };

        longPressTimer = setTimeout(() => {
            longPressTimer = null;
            longPressPhase = 'fired';
            clearShowTimer();
            clearHideTimer();
            model.value = next;
            anchorRect.value = anchorEl.getBoundingClientRect();
            isOpen.value = true;
        }, LONG_PRESS_MS);
    }

    /**
     * Cancels the pending long-press once the touch travels beyond TOUCH_MOVE_CANCEL_PX in either axis,
     * and records that it was travel — not a stationary lift — that ended it.
     *
     * Deliberately not gated on `longPressTimer !== null`: travel must still be recorded after the timer has
     * been cleared, otherwise the outcome degrades back to an unlabelled 'tap'. Travel after the press has
     * already fired is ignored — the card is open and the lift still needs its synthetic click suppressed.
     */
    function moveLongPress(point: { x: number; y: number }) {
        if (longPressPhase !== 'pending' || longPressOrigin.value === null) {
            return;
        }

        const dx = Math.abs(point.x - longPressOrigin.value.x);
        const dy = Math.abs(point.y - longPressOrigin.value.y);

        if (dx > TOUCH_MOVE_CANCEL_PX || dy > TOUCH_MOVE_CANCEL_PX) {
            longPressPhase = 'cancelled';
            clearLongPressTimer();
        }
    }

    /**
     * Ends the gesture and reports how it ended. Three outcomes, not a boolean:
     *  - 'fired'     — the press reached LONG_PRESS_MS and the card is open; suppress the synthetic click.
     *  - 'cancelled' — the finger travelled past TOUCH_MOVE_CANCEL_PX, or the gesture was invalidated by a
     *                  drag block / scroll / view change. This was never a tap, so open nothing.
     *  - 'tap'       — a clean stationary lift before LONG_PRESS_MS.
     *
     * Collapsing 'cancelled' into 'tap' is what let a swipe that began on a maintenance event pop a card open
     * on finger lift, contradicting the no-hijack contract this gesture is built around.
     */
    function cancelLongPress(): LongPressOutcome {
        const outcome: LongPressOutcome = longPressPhase === 'fired' ? 'fired' : longPressPhase === 'cancelled' ? 'cancelled' : 'tap';
        clearLongPressTimer();
        longPressPhase = 'idle';
        longPressOrigin.value = null;
        return outcome;
    }

    /** Re-reads the anchor geometry for the currently open card. */
    function updateAnchor(anchorEl: HTMLElement) {
        if (!isOpen.value) {
            return;
        }
        anchorRect.value = anchorEl.getBoundingClientRect();
    }

    function handleHoverQueryChange(event: MediaQueryListEvent) {
        isTouch.value = event.matches;
    }

    // The card is pinned to a rect, so any scroll or resize invalidates its placement.
    function handleViewportChange() {
        closeNow();
    }

    onMounted(() => {
        // Superset of '(hover: none)': catches coarse-primary-pointer devices whose browser still reports a
        // hover capability. It deliberately does NOT use '(any-pointer: coarse)' -- that matches any touchscreen
        // laptop and would suppress the mouse hover path on hardware where the mouse is the primary pointer.
        // Such hybrids therefore still evaluate to false here; CalendarView.vue covers them by attaching the
        // touch handlers unconditionally and filtering touch-synthesised mouse events instead.
        hoverQuery = window.matchMedia('(hover: none), (pointer: coarse)');
        isTouch.value = hoverQuery.matches;
        hoverQuery.addEventListener('change', handleHoverQueryChange);
        window.addEventListener('scroll', handleViewportChange, { capture: true, passive: true });
        window.addEventListener('resize', handleViewportChange);
    });

    onUnmounted(() => {
        window.removeEventListener('scroll', handleViewportChange, true);
        window.removeEventListener('resize', handleViewportChange);
        if (hoverQuery !== null) {
            hoverQuery.removeEventListener('change', handleHoverQueryChange);
            hoverQuery = null;
        }
        unblockGesture();
        clearShowTimer();
        clearHideTimer();
        clearLongPressTimer();
    });

    return {
        isOpen,
        model,
        anchorRect,
        isTouch,
        requestShow,
        openNow,
        requestHide,
        cancelHide,
        closeNow,
        blockGesture,
        unblockGesture,
        startLongPress,
        moveLongPress,
        cancelLongPress,
        updateAnchor
    };
}
